'use client';
import { useQuery } from '@tanstack/react-query';
import { mapStrapiSeo, ProcessedStrapiSeo, StrapiSeoRaw } from '@/services/strapi/strapiHomeSeo';

// Interfaces para los datos de Strapi
interface StrapiTextChild {
  text: string;
  type: string;
  bold?: boolean;
}

interface StrapiTextParagraph {
  type: string;
  children: StrapiTextChild[];
}

interface StrapiCategoriaRecomendada {
  id: number;
  Titulo: string;
  Color_Fondo_Hex?: string;
  Url?: string;
  Visible?: boolean | null;
  Imagen_Fondo?: {
    id: number;
    documentId: string;
    name: string;
    alternativeText?: string | null;
    caption?: string | null;
    width: number;
    height: number;
    formats?: any;
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl?: string | null;
    provider: string;
    provider_metadata?: any;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  };
  Imagen_Superior?: {
    id: number;
    documentId: string;
    name: string;
    alternativeText?: string | null;
    caption?: string | null;
    width: number;
    height: number;
    formats?: any;
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl?: string | null;
    provider: string;
    provider_metadata?: any;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  };
}

interface StrapiProductoMasVendido {
  id: number;
  Prestashop_ID_Producto: number | null;
  Prestashop_Nombre_Producto: string | null;
}

interface StrapiLoMasVendido {
  id: number;
  Titulo_Seccion: StrapiTextParagraph[];
  Visible: boolean;
  Productos: StrapiProductoMasVendido[];
}

interface StrapiNewsletterBannerMedia {
  id: number;
  documentId?: string;
  name?: string;
  url: string;
  alternativeText?: string | null;
  width?: number | null;
  height?: number | null;
  formats?: Record<string, unknown> | null;
}

/** Componente home.newsletter-banner (mismo esquema para Desktop y Mobile) */
interface StrapiNewsletterBanner {
  id?: number;
  Activo: boolean | null;
  Titulo: string | null;
  Descripcion: string | null;
  Alineacion_titulo: string | null;
  Posicion_titulo: string | null;
  Fuente_titulo: string | null;
  Boton: boolean | null;
  Url: string | null;
  Posicion_boton: string | null;
  Objeto_fondo: StrapiNewsletterBannerMedia | null;
  Objeto_superior: StrapiNewsletterBannerMedia | null;
  Objeto_fondo_ajuste: string | null;
  Objeto_superior_posicion: string | null;
  Color_Fondo_Hex: string | null;
  Color_Texto_Titulo_Hex: string | null;
  Color_Texto_Descripcion_Hex: string | null;
  Texto_boton: string | null;
}

interface StrapiInfoCard {
  id: number;
  Titulo: string;
  Subtitulo: string;
  Descripcion: string;
  Visible?: boolean;
  Url?: string | null;
  Imagen?: {
    id: number;
    name: string;
    url: string;
    formats?: {
      thumbnail?: { url: string };
      large?: { url: string };
      medium?: { url: string };
      small?: { url: string };
    };
  };
}

interface StrapiHiperaliaInfo {
  id: number;
  Titulo: StrapiTextParagraph[];
  Parrafo_Izquierda?: StrapiTextParagraph[];
  Parrafo_Derecha?: StrapiTextParagraph[];
  Visible?: boolean | null;
  Imagen?: {
    id: number;
    name: string;
    url: string;
    formats?: {
      thumbnail?: { url: string };
      large?: { url: string };
      medium?: { url: string };
      small?: { url: string };
    };
  };
}

interface StrapiHomeData {
  id: number;
  documentId: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  Categorias_Recomendadas: StrapiCategoriaRecomendada[];
  Lo_Mas_Vendido: StrapiLoMasVendido;
  Newsletter_Desktop: StrapiNewsletterBanner | null;
  Newsletter_Mobile: StrapiNewsletterBanner | null;
  Info_Cards: StrapiInfoCard[];
  Hiperalia_Info: StrapiHiperaliaInfo;
  SEO?: StrapiSeoRaw | null;
}

interface StrapiResponse {
  data: StrapiHomeData;
  meta: any;
}

// Interfaces para los datos procesados
export interface ProcessedNewsletterBanner {
  visible: boolean;
  titulo: string | null;
  descripcion: string | null;
  alineacionTitulo: string | null;
  posicionTitulo: string | null;
  fuenteTitulo: string | null;
  boton: boolean | null;
  url: string | null;
  posicionBoton: string | null;
  objetoFondo: StrapiNewsletterBannerMedia | null;
  objetoSuperior: StrapiNewsletterBannerMedia | null;
  objetoFondoAjuste: string | null;
  objetoSuperiorPosicion: string | null;
  colorFondoHex: string | null;
  colorTextoTituloHex: string | null;
  colorTextoDescripcionHex: string | null;
  textoBoton: string | null;
}

interface ProcessedInfoCard {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  visible?: boolean;
  url?: string | null;
  imagen?: {
    id: number;
    name: string;
    url: string;
    formats?: {
      thumbnail?: { url: string };
      large?: { url: string };
      medium?: { url: string };
      small?: { url: string };
    };
  };
}

interface ProcessedHiperaliaInfo {
  title: StrapiTextParagraph[];
  subtitle1: StrapiTextParagraph[];
  subtitle2: StrapiTextParagraph[];
  visible: boolean;
  imagen?: {
    id: number;
    name: string;
    url: string;
    formats?: {
      thumbnail?: { url: string };
      large?: { url: string };
      medium?: { url: string };
      small?: { url: string };
    };
  };
}

interface ProcessedCategoriaRecomendada {
  id: number;
  title: string;
  backgroundColor?: string;
  url?: string;
  visible: boolean;
  imagenFondo?: {
    id: number;
    name: string;
    url: string;
    width: number;
    height: number;
    alternativeText?: string | null;
  };
  imagenSuperior?: {
    id: number;
    name: string;
    url: string;
    width: number;
    height: number;
    alternativeText?: string | null;
  };
}

interface ProcessedLoMasVendido {
  id: number;
  title: StrapiTextParagraph[];
  visible: boolean;
  productos: {
    id: number;
    id_producto_prestashop: number | null;
    nombre_prestashop: string | null;
  }[];
}

export interface ProcessedHomeData {
  categoriasRecomendadas: ProcessedCategoriaRecomendada[];
  loMasVendido: ProcessedLoMasVendido | null;
  newsletterDesktop: ProcessedNewsletterBanner | null;
  newsletterMobile: ProcessedNewsletterBanner | null;
  infoCards: ProcessedInfoCard[];
  hiperaliaInfo: ProcessedHiperaliaInfo;
  seo: ProcessedStrapiSeo | null;
}

interface UseStrapiHomeReturn {
  data: ProcessedHomeData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Función para validar y limpiar datos de texto de Strapi
const sanitizeStrapiText = (text: any): StrapiTextParagraph[] => {
  if (!text || !Array.isArray(text)) {
    console.warn('StrapiText no es un array válido:', text);
    return [];
  }
  
  return text.filter(paragraph => 
    paragraph && 
    typeof paragraph === 'object' && 
    paragraph.type && 
    paragraph.children && 
    Array.isArray(paragraph.children)
  );
};

const fetchStrapiData = async (): Promise<ProcessedHomeData> => {
  const [responseHome, responseNewsletter] = await Promise.all([
    fetch('/api/strapi/home', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }),
    fetch('/api/strapi/home/newsletter', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }),
  ]);

  if (!responseHome.ok) {
    let errorMessage = `Error HTTP: ${responseHome.status}`;
    try {
      const errorData = await responseHome.json();
      errorMessage = errorData.error || errorMessage;
    } catch {
      // ignore
    }
    throw new Error(errorMessage);
  }

  let jsonData: StrapiResponse;
  try {
    jsonData = await responseHome.json();
  } catch (jsonError) {
    console.error('Error parsing Strapi response:', jsonError);
    throw new Error('Error al parsear respuesta de Strapi');
  }

  if (jsonData.data) {
    const homeData = jsonData.data;

    // Newsletter: leer Desktop y Mobile; la ruta /newsletter enriquece con medios si los trae.
    let rawDesktop = homeData.Newsletter_Desktop;
    let rawMobile = homeData.Newsletter_Mobile;
    if (responseNewsletter.ok) {
      try {
        const jsonNewsletter = await responseNewsletter.json();
        rawDesktop = jsonNewsletter?.data?.Newsletter_Desktop ?? rawDesktop;
        rawMobile = jsonNewsletter?.data?.Newsletter_Mobile ?? rawMobile;
      } catch {
        // mantener los de homeData si falla el parse
      }
    }

    const mapNewsletterBanner = (raw: StrapiNewsletterBanner | null | undefined): ProcessedNewsletterBanner | null => {
      if (!raw) return null;
      return {
        visible: raw.Activo ?? true,
        titulo: raw.Titulo ?? null,
        descripcion: raw.Descripcion ?? null,
        alineacionTitulo: raw.Alineacion_titulo ?? null,
        posicionTitulo: raw.Posicion_titulo ?? null,
        fuenteTitulo: raw.Fuente_titulo ?? null,
        boton: raw.Boton ?? null,
        url: raw.Url ?? null,
        posicionBoton: raw.Posicion_boton ?? null,
        objetoFondo: raw.Objeto_fondo ?? null,
        objetoSuperior: raw.Objeto_superior ?? null,
        objetoFondoAjuste: raw.Objeto_fondo_ajuste ?? null,
        objetoSuperiorPosicion: raw.Objeto_superior_posicion ?? null,
        colorFondoHex: raw.Color_Fondo_Hex ?? null,
        colorTextoTituloHex: raw.Color_Texto_Titulo_Hex ?? null,
        colorTextoDescripcionHex: raw.Color_Texto_Descripcion_Hex ?? null,
        textoBoton: raw.Texto_boton ?? null,
      };
    };

    const newsletterDesktop = mapNewsletterBanner(rawDesktop);
    const newsletterMobile = mapNewsletterBanner(rawMobile);

    // Procesar info cards
    const infoCards: ProcessedInfoCard[] = (homeData.Info_Cards || []).map(card => ({
      id: card.id,
      title: card.Titulo,
      subtitle: card.Subtitulo,
      description: card.Descripcion,
      visible: card.Visible,
      url: card.Url,
      imagen: card.Imagen
    }));

    // Procesar categorías recomendadas
    const categoriasRecomendadas: ProcessedCategoriaRecomendada[] = (homeData.Categorias_Recomendadas || []).map(categoria => ({
      id: categoria.id,
      title: categoria.Titulo,
      backgroundColor: categoria.Color_Fondo_Hex ? `#${categoria.Color_Fondo_Hex.replace('#', '')}` : undefined,
      url: categoria.Url,
      visible: categoria.Visible ?? true, // Default true para retrocompatibilidad
      imagenFondo: categoria.Imagen_Fondo ? {
        id: categoria.Imagen_Fondo.id,
        name: categoria.Imagen_Fondo.name,
        url: categoria.Imagen_Fondo.url,
        width: categoria.Imagen_Fondo.width,
        height: categoria.Imagen_Fondo.height,
        alternativeText: categoria.Imagen_Fondo.alternativeText
      } : undefined,
      imagenSuperior: categoria.Imagen_Superior ? {
        id: categoria.Imagen_Superior.id,
        name: categoria.Imagen_Superior.name,
        url: categoria.Imagen_Superior.url,
        width: categoria.Imagen_Superior.width,
        height: categoria.Imagen_Superior.height,
        alternativeText: categoria.Imagen_Superior.alternativeText
      } : undefined
    }));

    // Procesar Lo Más Vendido
    let loMasVendido: ProcessedLoMasVendido | null = null;
    if (homeData.Lo_Mas_Vendido) {
      loMasVendido = {
        id: homeData.Lo_Mas_Vendido.id,
        title: sanitizeStrapiText(homeData.Lo_Mas_Vendido.Titulo_Seccion),
        visible: homeData.Lo_Mas_Vendido.Visible ?? true, // Default true para retrocompatibilidad
        productos: (homeData.Lo_Mas_Vendido.Productos || []).map(producto => ({
          id: producto.id,
          id_producto_prestashop: producto.Prestashop_ID_Producto,
          nombre_prestashop: producto.Prestashop_Nombre_Producto
        }))
      };
    }

    // Procesar información de Hiperalia
    const hiperaliaInfo: ProcessedHiperaliaInfo = {
      title: sanitizeStrapiText(homeData.Hiperalia_Info?.Titulo),
      subtitle1: sanitizeStrapiText(homeData.Hiperalia_Info?.Parrafo_Izquierda),
      subtitle2: sanitizeStrapiText(homeData.Hiperalia_Info?.Parrafo_Derecha),
      visible: homeData.Hiperalia_Info?.Visible ?? true, // Default true para retrocompatibilidad
      imagen: homeData.Hiperalia_Info?.Imagen
    };

    const processedData: ProcessedHomeData = {
      categoriasRecomendadas,
      loMasVendido,
      newsletterDesktop,
      newsletterMobile,
      infoCards,
      hiperaliaInfo,
      seo: mapStrapiSeo(homeData.SEO),
    };

    return processedData;
  } else {
    throw new Error('No se encontraron datos en la respuesta');
  }
};

const useStrapiHome = (): UseStrapiHomeReturn => {
  const {
    data,
    isLoading: loading,
    error,
    refetch
  } = useQuery<ProcessedHomeData, Error>({
    queryKey: ['strapi-home'],
    queryFn: fetchStrapiData,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos (gcTime reemplaza a cacheTime en v5)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    data: data || null,
    loading,
    error: error?.message || null,
    refetch: () => refetch()
  };
};

export default useStrapiHome;
